import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, Search, ShieldCheck } from "lucide-react";
import { FadeUp, SectionHeading } from "@/components/Reveal";
import InquiryDialog from "@/components/InquiryDialog";
import { HOSTING_PLANS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DomainInquiry = () => {
  const [form, setForm] = useState({ domain: "", name: "", email: "", website: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/domain-inquiries`, form);
      toast.success("Domain inquiry sent — we'll confirm availability and pricing.");
      setForm({ domain: "", name: "", email: "", website: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <FadeUp className="mt-16">
      <div className="rounded-2xl border border-white/10 bg-[#0C0C0F] p-8 lg:p-10">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="font-display text-xl font-bold text-white sm:text-2xl">
              Looking for a domain?
            </h3>
            <p className="mt-2 text-sm text-[#A1A1AA]">
              Domains are inquiry-only — tell us the name you want and we confirm availability,
              pricing, and handle registration for you.
            </p>
          </div>
          <ShieldCheck className="hidden h-10 w-10 text-[#D9A92E] sm:block" />
        </div>
        <form onSubmit={submit} className="mt-6 grid gap-4 lg:grid-cols-[1fr_220px_220px_auto]">
          <input
            type="text"
            value={form.website}
            onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
            className="hidden"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />
          <input
            required
            placeholder="yourbusiness.com"
            value={form.domain}
            onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))}
            className="spark-input"
            data-testid="domain-search-input"
          />
          <input
            required
            placeholder="Your name *"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="spark-input"
            data-testid="domain-search-name"
          />
          <input
            required
            type="email"
            placeholder="Email *"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className="spark-input"
            data-testid="domain-search-email"
          />
          <button
            type="submit"
            disabled={sending}
            data-testid="domain-search-submit"
            className="flex items-center justify-center gap-2 rounded-xl bg-[#D9A92E] px-6 py-3 text-sm font-semibold text-white shadow-[0_0_25px_rgba(217,169,46,0.4)] transition-all duration-300 hover:bg-[#E8C05A] disabled:opacity-60"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Check
          </button>
        </form>
      </div>
    </FadeUp>
  );
};

const Hosting = () => {
  const [plan, setPlan] = useState(null);

  return (
    <section id="hosting" className="border-y border-white/10 bg-[#0C0C0F]" data-testid="hosting-section">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-12 lg:py-32">
        <SectionHeading
          eyebrow="Hosting & Domains"
          title="Fast hosting. Zero headaches."
          sub="High-speed SSD shared hosting with a 99.9% uptime guarantee, daily backups, and support from real humans — not ticket bots."
          center
        />
        <div className="mx-auto mt-14 grid max-w-4xl gap-6 md:grid-cols-2">
          {HOSTING_PLANS.map((p, i) => (
            <FadeUp key={p.id} delay={i * 0.1}>
              <div
                data-testid={p.testid}
                className={`relative flex h-full flex-col rounded-2xl border p-8 transition-colors duration-300 lg:p-10 ${
                  p.popular
                    ? "border-[#D9A92E]/50 bg-[#131316] shadow-[0_0_60px_rgba(217,169,46,0.12)]"
                    : "border-white/10 bg-[#131316] hover:border-white/25"
                }`}
              >
                {p.popular && (
                  <span className="mono absolute -top-3 left-8 rounded-full bg-[#D9A92E] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white">
                    Popular
                  </span>
                )}
                <h3 className="text-xl font-semibold text-white">{p.name}</h3>
                <p className="mt-2 text-sm text-[#A1A1AA]">{p.tagline}</p>
                <div className="mt-6 flex items-end gap-1">
                  <span className="font-display text-5xl font-extrabold text-white">${p.price}</span>
                  <span className="pb-1.5 text-sm text-[#6B7280]">/year</span>
                </div>
                <ul className="mt-8 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-center gap-3 text-sm text-[#A1A1AA]">
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#D9A92E]/12">
                        <Check className="h-3 w-3 text-[#D9A92E]" />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => setPlan(p.name)}
                  data-testid={`${p.testid}-cta`}
                  className={`mt-10 rounded-full px-6 py-3.5 text-sm font-semibold transition-all duration-300 ${
                    p.popular
                      ? "bg-[#D9A92E] text-[#09090B] shadow-[0_0_25px_rgba(217,169,46,0.4)] hover:bg-[#E8C05A] hover:shadow-[0_0_35px_rgba(217,169,46,0.6)]"
                      : "border border-white/15 text-white hover:border-[#D9A92E]/60 hover:text-[#E9C766]"
                  }`}
                >
                  Get {p.name.replace("Shared Hosting ", "")}
                </button>
              </div>
            </FadeUp>
          ))}
        </div>
        <DomainInquiry />
      </div>
      <InquiryDialog
        open={!!plan}
        onOpenChange={() => setPlan(null)}
        service="hosting-domain"
        plan={plan || ""}
      />
    </section>
  );
};

export default Hosting;
